import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, InputNumber, Select, Switch, Tag, Modal, Empty, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api_product } from '../../api/product';
import Code128Barcode from '../common/code128-barcode';
import { printDocument } from '../../utils/print-utils';
import '../../assets/css/barcode-print.css';

interface PrintItem {
    id: number;
    product_code: string;
    product_name: string;
    price_retail: number | string;
    copies: number;
}

export type PrintRotation = 0 | 90 | 180 | 270;

export const BarcodePrint: React.FC = () => {
    const navigate = useNavigate();
    const barcodeGridRef = useRef<HTMLDivElement>(null);
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState<PrintItem[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [storeName, setStoreName] = useState('JINDA POS');

    // Label layout configurations
    const [showStoreName, setShowStoreName] = useState(true);
    const [showProductName, setShowProductName] = useState(true);
    const [showPrice, setShowPrice] = useState(true);
    const [barcodeSize, setBarcodeSize] = useState<'38x28' | '40x30' | '30x40' | 'standard' | 'compact' | 'large' | 'thermal58' | 'a4' | 'custom'>('38x28');
    const [a4GridSize, setA4GridSize] = useState<'38x28' | '50x30' | '65x35' | '80x50'>('38x28');
    const [printColorMode, setPrintColorMode] = useState<'color' | 'mono'>('mono');
    const [rotation, setRotation] = useState<PrintRotation>(() => {
        const saved = localStorage.getItem('jinda_print_rotation');
        return saved && [0, 90, 180, 270].includes(Number(saved)) ? (Number(saved) as PrintRotation) : 0;
    });

    const handleRotationChange = (val: PrintRotation) => {
        setRotation(val);
        try { localStorage.setItem('jinda_print_rotation', String(val)); } catch (e) {}
    };

    const [customWidth, setCustomWidth] = useState<number>(() => {
        const saved = localStorage.getItem('jinda_barcode_custom_w');
        return saved && Number(saved) > 0 ? Number(saved) : 38;
    });
    const [customHeight, setCustomHeight] = useState<number>(() => {
        const saved = localStorage.getItem('jinda_barcode_custom_h');
        return saved && Number(saved) > 0 ? Number(saved) : 28;
    });

    const handleCustomWidthChange = (val: number | null) => {
        const num = val && val > 0 ? val : 38;
        setCustomWidth(num);
        try { localStorage.setItem('jinda_barcode_custom_w', String(num)); } catch (e) {}
    };

    const handleCustomHeightChange = (val: number | null) => {
        const num = val && val > 0 ? val : 28;
        setCustomHeight(num);
        try { localStorage.setItem('jinda_barcode_custom_h', String(num)); } catch (e) {}
    };

    const getEffectiveDimensions = () => {
        switch (barcodeSize) {
            case '38x28':
                return { width: 38, height: 28, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 38, a4CardH: 28 };
            case '40x30':
                return { width: 40, height: 30, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 40, a4CardH: 30 };
            case '30x40':
                return { width: 30, height: 40, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 30, a4CardH: 40 };
            case 'compact':
                return { width: 35, height: 25, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 35, a4CardH: 25 };
            case 'standard':
                return { width: 50, height: 30, isSmall: false, isContinuous: false, isA4: false, a4Cols: 3, a4CardW: 50, a4CardH: 30 };
            case 'large':
                return { width: 60, height: 40, isSmall: false, isContinuous: false, isA4: false, a4Cols: 3, a4CardW: 60, a4CardH: 40 };
            case 'thermal58':
                return { width: 58, height: 0, isSmall: false, isContinuous: true, isA4: false, a4Cols: 1, a4CardW: 58, a4CardH: 0 };
            case 'a4': {
                const colCount = a4GridSize === '80x50' ? 2 : a4GridSize === '65x35' || a4GridSize === '50x30' ? 3 : 4;
                const cardH = a4GridSize === '80x50' ? 50 : a4GridSize === '65x35' ? 35 : a4GridSize === '50x30' ? 30 : 28;
                const cardW = a4GridSize === '80x50' ? 80 : a4GridSize === '65x35' ? 65 : a4GridSize === '50x30' ? 50 : 38;
                return { width: 210, height: 297, isSmall: cardW <= 45, isContinuous: false, isA4: true, a4Cols: colCount, a4CardW: cardW, a4CardH: cardH };
            }
            case 'custom': {
                const w = Math.max(10, Number(customWidth) || 38);
                const h = Math.max(10, Number(customHeight) || 28);
                return { width: w, height: h, isSmall: w <= 45, isContinuous: false, isA4: false, a4Cols: 3, a4CardW: w, a4CardH: h };
            }
            default:
                return { width: 38, height: 28, isSmall: true, isContinuous: false, isA4: false, a4Cols: 4, a4CardW: 38, a4CardH: 28 };
        }
    };

    useEffect(() => {
        if (!searchQuery.trim()) {
            loadProducts();
            return;
        }
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api_product('LIST', { search: searchQuery.trim(), page: 1, page_size: 100 });
                const list = Array.isArray(res) ? res : (res?.items || []);
                if (Array.isArray(list)) {
                    setProducts(list);
                }
            } catch (e) {
                console.error('Failed to search products', e);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const res = await api_product('LIST', { page: 1, page_size: 100 });
            const list = Array.isArray(res) ? res : (res?.items || []);
            if (Array.isArray(list)) {
                setProducts(list);
            }
        } catch (e) {
            console.error('Failed to load products', e);
        } finally {
            setLoading(false);
        }
    };

    const filteredProductOptions = useMemo(() => {
        return products;
    }, [products]);

    const handleAddProduct = (productId: number) => {
        const prod = products.find(p => p.id === productId);
        if (!prod || !prod.product_code) return;

        const existingIndex = selectedItems.findIndex(item => item.id === prod.id);
        if (existingIndex >= 0) {
            const updated = [...selectedItems];
            updated[existingIndex].copies += 1;
            setSelectedItems(updated);
        } else {
            setSelectedItems([...selectedItems, {
                id: prod.id,
                product_code: prod.product_code,
                product_name: prod.product_name,
                price_retail: prod.price_retail || 0,
                copies: 1
            }]);
        }
    };

    const handleAddAllFiltered = () => {
        const newItems: PrintItem[] = [...selectedItems];
        filteredProductOptions.forEach(prod => {
            if (!prod.product_code) return;
            const idx = newItems.findIndex(i => i.id === prod.id);
            if (idx >= 0) {
                newItems[idx].copies += 1;
            } else {
                newItems.push({
                    id: prod.id,
                    product_code: prod.product_code,
                    product_name: prod.product_name,
                    price_retail: prod.price_retail || 0,
                    copies: 1
                });
            }
        });
        setSelectedItems(newItems);
    };

    const handleUpdateCopies = (id: number, copies: number) => {
        const updated = selectedItems.map(item =>
            item.id === id ? { ...item, copies: Math.max(1, copies) } : item
        );
        setSelectedItems(updated);
    };

    const handleRemoveItem = (id: number) => {
        setSelectedItems(selectedItems.filter(item => item.id !== id));
    };

    const handleClearAll = () => {
        setSelectedItems([]);
    };

    const totalLabelsCount = useMemo(() => {
        return selectedItems.reduce((sum, item) => sum + (item.copies || 0), 0);
    }, [selectedItems]);

    // Flatten items according to copy count for rendering
    const previewLabels = useMemo(() => {
        const labels: PrintItem[] = [];
        selectedItems.forEach(item => {
            for (let i = 0; i < (item.copies || 1); i++) {
                labels.push(item);
            }
        });
        return labels;
    }, [selectedItems]);

    const handlePrint = () => {
        if (previewLabels.length === 0) {
            Modal.warning({
                title: 'ยังไม่มีรายการสำหรับพิมพ์',
                content: 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการก่อนสั่งพิมพ์',
                okText: 'เข้าใจแล้ว'
            });
            return;
        }
        if (barcodeGridRef.current) {
            const dims = getEffectiveDimensions();
            let docType: any = 'barcode-label';
            let customPrintCss = '';

            if (dims.isContinuous) {
                docType = 'thermal-58mm';
            } else if (dims.isA4) {
                docType = 'a4-portrait';
                const isColor = printColorMode === 'color';
                const cols = (dims as any).a4Cols || 4;
                const cardH = (dims as any).a4CardH || 28;
                customPrintCss = `
                    @page {
                        size: A4 portrait;
                        margin: 8mm 6mm;
                    }
                    * {
                        box-sizing: border-box !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #FFFFFF !important;
                        color: #000000 !important;
                    }
                    .no-print, .no-print * {
                        display: none !important;
                    }
                    .barcode-preview-container {
                        background: transparent !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        border: none !important;
                    }
                    .barcode-preview-grid {
                        display: grid !important;
                        grid-template-columns: repeat(${cols}, 1fr) !important;
                        gap: 2.5mm 2.5mm !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: 100% !important;
                    }
                    .barcode-label-card {
                        width: 100% !important;
                        height: ${cardH}mm !important;
                        max-height: ${cardH}mm !important;
                        border: 0.5px dashed ${isColor ? '#FF7A59' : '#CBD5E1'} !important;
                        border-radius: 4px !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                        display: flex !important;
                        align-items: center !important;
                        justify-content: center !important;
                    }
                    .barcode-label-inner {
                        width: 100% !important;
                        height: 100% !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        justify-content: space-between !important;
                        box-sizing: border-box !important;
                        padding: ${cardH <= 30 ? '0.8mm 1.2mm' : '1.5mm 2mm'} !important;
                    }
                    .barcode-label-store {
                        font-size: ${cardH <= 30 ? '7.5pt' : '9.5pt'} !important;
                        font-weight: 800 !important;
                        color: ${isColor ? '#FF7A59' : '#000000'} !important;
                        line-height: 1.1 !important;
                        margin-bottom: 0.2mm !important;
                        text-align: center !important;
                    }
                    .barcode-label-name {
                        font-size: ${cardH <= 30 ? '8.5pt' : '11pt'} !important;
                        line-height: 1.15 !important;
                        max-height: 2.3em !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        margin-bottom: 0.2mm !important;
                        font-weight: 900 !important;
                        color: #000000 !important;
                        text-align: center !important;
                        width: 100% !important;
                    }
                    .barcode-label-price {
                        font-size: ${cardH <= 30 ? '11pt' : '14pt'} !important;
                        line-height: 1 !important;
                        font-weight: 900 !important;
                        color: ${isColor ? '#FF7A59' : '#000000'} !important;
                        margin-top: 0.2mm !important;
                        text-align: center !important;
                    }
                    .barcode-svg-wrapper svg {
                        max-width: 100% !important;
                        max-height: ${cardH <= 30 ? '11mm' : '16mm'} !important;
                        display: block !important;
                        margin: 0 auto !important;
                    }
                `;
                // Sticker label sizes (38x28, 40x30, 30x40, 60x40, 80x50, 100x70, or Custom mm)
                docType = 'custom';
                const isSmall = dims.isSmall;
                const isRotated90or270 = rotation === 90 || rotation === 270;
                const safeWidth = Math.max(10, dims.width - 0.6);
                const safeHeight = Math.max(10, dims.height - 0.6);
                const innerW = isRotated90or270 ? safeHeight : safeWidth;
                const innerH = isRotated90or270 ? safeWidth : safeHeight;
                let transformCss = 'none';
                if (rotation === 90) {
                    transformCss = `translateX(${safeWidth}mm) rotate(90deg)`;
                } else if (rotation === 270) {
                    transformCss = `translateY(${safeHeight}mm) rotate(270deg)`;
                } else if (rotation === 180) {
                    transformCss = `translate(${safeWidth}mm, ${safeHeight}mm) rotate(180deg)`;
                }

                customPrintCss = `
                    @page {
                        size: ${dims.width}mm ${dims.height}mm;
                        margin: 0 !important;
                    }
                    * {
                        box-sizing: border-box !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #FFFFFF !important;
                        color: #000000 !important;
                        width: ${dims.width}mm !important;
                        height: auto !important;
                        overflow: hidden !important;
                    }
                    .no-print, .no-print * {
                        display: none !important;
                    }
                    .barcode-preview-container {
                        background: transparent !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        border: none !important;
                    }
                    .barcode-preview-grid {
                        display: block !important;
                        padding: 0 !important;
                        margin: 0 !important;
                        width: ${dims.width}mm !important;
                        font-size: 0 !important;
                        line-height: 0 !important;
                        height: auto !important;
                    }
                    .barcode-label-card {
                        width: ${dims.width}mm !important;
                        height: ${safeHeight}mm !important;
                        max-width: ${dims.width}mm !important;
                        max-height: ${safeHeight}mm !important;
                        min-width: 0 !important;
                        min-height: 0 !important;
                        position: relative !important;
                        box-sizing: border-box !important;
                        padding: 0 !important;
                        margin: 0 auto !important;
                        border: none !important;
                        border-radius: 0 !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                        contain: strict !important;
                    }
                    .barcode-label-card:not(:last-child) {
                        page-break-after: always !important;
                        break-after: page !important;
                    }
                    .barcode-label-card:last-child {
                        page-break-after: avoid !important;
                        break-after: avoid !important;
                    }
                    .barcode-label-inner {
                        position: ${rotation !== 0 ? 'absolute' : 'relative'} !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: ${innerW}mm !important;
                        height: ${innerH}mm !important;
                        max-width: ${innerW}mm !important;
                        max-height: ${innerH}mm !important;
                        transform: ${transformCss} !important;
                        transform-origin: 0 0 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                        justify-content: center !important;
                        gap: ${isSmall ? '0.2mm' : '0.6mm'} !important;
                        box-sizing: border-box !important;
                        padding: ${isSmall ? '0.4mm 0.8mm' : '1.5mm 2.5mm'} !important;
                        overflow: hidden !important;
                        background: #FFFFFF !important;
                    }
                    .barcode-label-store {
                        font-size: ${isSmall ? '8.5pt' : '10pt'} !important;
                        font-weight: 800 !important;
                        color: #000000 !important;
                        line-height: 1.05 !important;
                        margin: 0 0 ${isSmall ? '0.1mm' : '0.3mm'} 0 !important;
                        white-space: nowrap !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        max-width: 100% !important;
                        text-align: center !important;
                        display: block !important;
                    }
                    .barcode-label-name {
                        font-size: ${isSmall ? '9.5pt' : '12pt'} !important;
                        line-height: 1.05 !important;
                        max-height: ${isSmall ? '2.2em' : '3.2em'} !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        margin: 0 0 ${isSmall ? '0.1mm' : '0.3mm'} 0 !important;
                        font-weight: 800 !important;
                        color: #000000 !important;
                        width: 100% !important;
                        text-align: center !important;
                        display: block !important;
                    }
                    .barcode-label-price {
                        font-size: ${isSmall ? '13pt' : '18pt'} !important;
                        line-height: 1.0 !important;
                        font-weight: 900 !important;
                        color: #000000 !important;
                        margin: ${isSmall ? '0.1mm 0 0 0' : '0.3mm 0 0 0'} !important;
                        text-align: center !important;
                        display: block !important;
                    }
                    .barcode-svg-wrapper svg {
                        max-width: 100% !important;
                        max-height: ${isSmall ? '9.5mm' : '14mm'} !important;
                        display: block !important;
                        margin: 0 auto !important;
                    }
                `;
            }

            printDocument(barcodeGridRef.current, {
                documentType: docType,
                title: 'พิมพ์สติ๊กเกอร์บาร์โค้ด',
                pageMargin: '0',
                customStyles: customPrintCss
            });
        }
    };

    const getBarcodeProps = () => {
        switch (barcodeSize) {
            case '40x30':
            case '30x40':
                return { height: 20, fontSize: 8.5 };
            case 'compact':
                return { height: 20, fontSize: 8.5 };
            case 'thermal58':
                return { height: 28, fontSize: 10 };
            case 'large':
                return { height: 44, fontSize: 13 };
            case 'a4':
                return { height: 32, fontSize: 11 };
            case 'custom': {
                const dims = getEffectiveDimensions();
                return {
                    height: dims.height <= 30 ? 20 : dims.height <= 40 ? 28 : 44,
                    fontSize: dims.width <= 40 ? 8.5 : 11
                };
            }
            default:
                return { height: 34, fontSize: 11 };
        }
    };

    return (
        <div className="barcode-print-page">
            {/* Top Navigation & Action Header */}
            <div className="barcode-header no-print">
                <div className="barcode-header-title">
                    <Button
                        type="default"
                        icon={<FontAwesomeIcon icon="arrow-left" />}
                        onClick={() => navigate('/product')}
                        className="btn-back"
                        data-testid="btn-back-to-products"
                    >
                        กลับ
                    </Button>
                    <h2>🏷️ พิมพ์บาร์โค้ดสินค้า (Barcode Printing)</h2>
                </div>
                <div className="barcode-header-actions">
                    <Button
                        type="default"
                        danger
                        icon={<FontAwesomeIcon icon="trash-can" />}
                        onClick={handleClearAll}
                        disabled={selectedItems.length === 0}
                        data-testid="btn-clear-all"
                    >
                        ล้างรายการ ({selectedItems.length})
                    </Button>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon="print" />}
                        onClick={handlePrint}
                        disabled={previewLabels.length === 0}
                        style={{
                            background: 'linear-gradient(135deg, #FFA07A 0%, #FF6B4A 100%)',
                            border: 'none',
                            fontWeight: 700,
                            borderRadius: '10px'
                        }}
                        data-testid="btn-trigger-print"
                    >
                        🖨️ สั่งพิมพ์บาร์โค้ด ({totalLabelsCount} ดวง)
                    </Button>
                </div>
            </div>

            <div className="barcode-layout">
                {/* Left Side: Product Selector & Control Panel */}
                <div className="barcode-control-panel no-print">
                    <div className="barcode-card">
                        <div className="barcode-card-title">
                            <span>🔍 เลือกสินค้าที่ต้องการพิมพ์</span>
                            {filteredProductOptions.length > 0 && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={handleAddAllFiltered}
                                    style={{ padding: 0 }}
                                    data-testid="btn-add-all-filtered"
                                >
                                    + เพิ่มทั้งหมด ({filteredProductOptions.length})
                                </Button>
                            )}
                        </div>

                        <div className="barcode-search-box">
                            <Input
                                placeholder="ค้นหาตามชื่อสินค้า หรือ รหัสบาร์โค้ด..."
                                prefix={<FontAwesomeIcon icon="magnifying-glass" style={{ color: '#94A3B8' }} />}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                allowClear
                                data-testid="input-search-product"
                            />
                        </div>

                        <Select
                            showSearch
                            placeholder="คลิกเพื่อค้นหาและเลือกสินค้า..."
                            style={{ width: '100%', marginBottom: '14px' }}
                            optionFilterProp="children"
                            onChange={handleAddProduct}
                            value={undefined}
                            data-testid="select-product-dropdown"
                        >
                            {filteredProductOptions.map(p => (
                                <Select.Option key={p.id} value={p.id}>
                                    [{p.product_code}] {p.product_name} - ฿{Number(p.price_retail || 0).toLocaleString()}
                                </Select.Option>
                            ))}
                        </Select>

                        <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                            {selectedItems.length === 0 ? (
                                <Empty description="ยังไม่ได้เลือกสินค้าเข้าสู่คิวพิมพ์" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                selectedItems.map(item => (
                                    <div key={item.id} className="barcode-item-row" data-testid={`selected-item-${item.id}`}>
                                        <div className="barcode-item-info">
                                            <div className="barcode-item-name" title={item.product_name}>
                                                {item.product_name}
                                            </div>
                                            <div className="barcode-item-code">
                                                <Tag color={item.product_code.startsWith('SKU') ? 'orange' : 'blue'}>
                                                    {item.product_code}
                                                </Tag>
                                                ฿{Number(item.price_retail).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="barcode-item-qty">
                                            <InputNumber
                                                min={1}
                                                max={500}
                                                value={item.copies}
                                                onChange={(val) => handleUpdateCopies(item.id, Number(val || 1))}
                                                style={{ width: '65px' }}
                                                data-testid={`input-copies-${item.id}`}
                                            />
                                            <Button
                                                type="text"
                                                danger
                                                icon={<FontAwesomeIcon icon="xmark" />}
                                                onClick={() => handleRemoveItem(item.id)}
                                                data-testid={`btn-remove-${item.id}`}
                                            />
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Label Options */}
                    <div className="barcode-card">
                        <div className="barcode-card-title">
                            <span>⚙️ ตั้งค่ารูปแบบป้ายสติกเกอร์</span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    ชื่อร้านค้าบนป้าย
                                </label>
                                <Input
                                    value={storeName}
                                    onChange={(e) => setStoreName(e.target.value)}
                                    placeholder="ชื่อร้าน..."
                                    disabled={!showStoreName}
                                    data-testid="input-store-name"
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงชื่อร้านค้า</span>
                                <Switch checked={showStoreName} onChange={setShowStoreName} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงชื่อสินค้า</span>
                                <Switch checked={showProductName} onChange={setShowProductName} />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px' }}>แสดงราคาสินค้า</span>
                                <Switch checked={showPrice} onChange={setShowPrice} />
                            </div>

                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    ขนาดกระดาษ / ป้ายสติกเกอร์
                                </label>
                                <Select
                                    value={barcodeSize}
                                    onChange={setBarcodeSize}
                                    style={{ width: '100%' }}
                                    options={[
                                        { value: '38x28', label: '38 × 28 mm (ขนาดสติ๊กเกอร์มาตรฐาน / ขนาดพิมพ์พอดีดวง)' },
                                        { value: '40x30', label: '40 × 30 mm (Xprinter XP-420B / สติกเกอร์ขนาดเล็ก)' },
                                        { value: '30x40', label: '30 × 40 mm (สลับด้าน - สำหรับ Driver บางรุ่น)' },
                                        { value: 'standard', label: '50 × 30 mm (มาตรฐาน - สติกเกอร์ความร้อนยอดนิยม)' },
                                        { value: 'compact', label: '35 × 25 mm (ดวงเล็ก - สติกเกอร์ขนาดเล็ก)' },
                                        { value: 'large', label: '60 × 40 mm (ดวงใหญ่ - กล่อง/สินค้าชิ้นใหญ่)' },
                                        { value: 'thermal58', label: 'ม้วนกระดาษความร้อน 58mm (Thermal Slip 58mm)' },
                                        { value: 'a4', label: 'กระดาษ A4 (สติกเกอร์ A4 จัดเรียง 24-40 ป้าย/แผ่น)' },
                                        { value: 'custom', label: '⚙️ กำหนดขนาดเอง (Custom Size)' }
                                    ]}
                                    data-testid="select-barcode-size"
                                />

                                {barcodeSize === 'custom' && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '10px',
                                        marginTop: '10px',
                                        padding: '12px',
                                        background: 'var(--bg-subtle, #F8FAFC)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color, #E2E8F0)'
                                    }} data-testid="custom-size-inputs">
                                        <div>
                                            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                                ความกว้าง (Width)
                                            </label>
                                            <InputNumber
                                                min={10}
                                                max={300}
                                                step={1}
                                                precision={1}
                                                value={customWidth}
                                                onChange={handleCustomWidthChange}
                                                addonAfter="mm"
                                                style={{ width: '100%' }}
                                                data-testid="input-custom-width"
                                            />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                                ความสูง (Height)
                                            </label>
                                            <InputNumber
                                                min={10}
                                                max={300}
                                                step={1}
                                                precision={1}
                                                value={customHeight}
                                                onChange={handleCustomHeightChange}
                                                addonAfter="mm"
                                                style={{ width: '100%' }}
                                                data-testid="input-custom-height"
                                            />
                                        </div>
                                    </div>
                                )}
                                {barcodeSize === 'a4' && (
                                    <div style={{
                                        marginTop: '10px',
                                        padding: '12px',
                                        background: 'var(--bg-subtle, #F8FAFC)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color, #E2E8F0)'
                                    }} data-testid="a4-barcode-template-size-box">
                                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                            📐 ขนาดแม่แบบสติ๊กเกอร์บนแผ่น A4
                                        </label>
                                        <Select
                                            value={a4GridSize}
                                            onChange={setA4GridSize}
                                            style={{ width: '100%' }}
                                            options={[
                                                { value: '38x28', label: '38 × 28 mm (4 คอลัมน์ ~ 36-40 ดวง/แผ่น - ดวงเล็ก)' },
                                                { value: '50x30', label: '50 × 30 mm (3 คอลัมน์ ~ 21-24 ดวง/แผ่น - ดวงกลาง)' },
                                                { value: '65x35', label: '65 × 35 mm (3 คอลัมน์ ~ 18-21 ดวง/แผ่น - สติ๊กเกอร์มาตรฐาน A4)' },
                                                { value: '80x50', label: '80 × 50 mm (2 คอลัมน์ ~ 8-10 ดวง/แผ่น - ป้ายใหญ่)' }
                                            ]}
                                            data-testid="select-a4-barcode-grid-size"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Print Color Mode (Color vs Black & White) */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    🎨 รูปแบบสีการพิมพ์ (Print Color Mode)
                                </label>
                                <Radio.Group 
                                    value={printColorMode} 
                                    onChange={(e) => setPrintColorMode(e.target.value)}
                                    buttonStyle="solid"
                                    style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}
                                    data-testid="radio-barcode-print-color-mode"
                                >
                                    <Radio.Button value="mono" style={{ textAlign: 'center', borderRadius: '8px', fontSize: '12.5px' }}>
                                        ⚫ ขาวดำ (สติ๊กเกอร์)
                                    </Radio.Button>
                                    <Radio.Button value="color" style={{ textAlign: 'center', borderRadius: '8px', fontSize: '12.5px' }}>
                                        🎨 พิมพ์สี (สำหรับ A4)
                                    </Radio.Button>
                                </Radio.Group>
                            </div>

                            {/* Rotation & Orientation Control */}
                            <div>
                                <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                    🔄 การหมุนป้าย / ทิศทางการพิมพ์ (Orientation & Rotation)
                                </label>
                                <Select
                                    value={rotation}
                                    onChange={handleRotationChange}
                                    style={{ width: '100%' }}
                                    options={[
                                        { value: 0, label: '0° (ปกติ - ไม่หมุน)' },
                                        { value: 90, label: '90° (หมุน 90° ตามเข็ม - แก้ปัญหาป้ายออกแนวนอน)' },
                                        { value: 270, label: '270° (หมุน 90° ทวนเข็ม)' },
                                        { value: 180, label: '180° (กลับหัว 180°)' }
                                    ]}
                                    data-testid="select-barcode-rotation"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Live Barcode Preview */}
                <div className="barcode-preview-container">
                    <div className="barcode-card-title no-print">
                        <span>👁️ ตัวอย่างป้ายบาร์โค้ดก่อนพิมพ์ (Preview - {previewLabels.length} ดวง | การหมุน: {rotation}°)</span>
                        <Tag color="cyan">Code 128 Vector SVG</Tag>
                    </div>

                    {previewLabels.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0' }} className="no-print">
                            <Empty description="กรุณาเลือกสินค้าเพื่อดูตัวอย่างบาร์โค้ดและพิมพ์" />
                        </div>
                    ) : (
                        <div ref={barcodeGridRef} className={`barcode-preview-grid grid-size-${barcodeSize}`} data-testid="barcode-preview-grid">
                            {previewLabels.map((item, index) => {
                                const dims = getEffectiveDimensions();
                                return (
                                    <div 
                                        key={`${item.id}-${index}`} 
                                        className={`barcode-label-card card-size-${barcodeSize}`}
                                        style={{
                                            aspectRatio: (!dims.isContinuous && !dims.isA4) ? `${dims.width} / ${dims.height}` : undefined,
                                            width: dims.isSmall ? '175px' : dims.width <= 55 ? '200px' : '240px',
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            background: '#FFFFFF'
                                        }}
                                    >
                                        <div
                                            className="barcode-label-inner"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: dims.isSmall ? '2px' : '4px',
                                                boxSizing: 'border-box',
                                                padding: dims.isSmall ? '4px 6px' : '8px 12px'
                                            }}
                                        >
                                            {showStoreName && storeName && (
                                                <div className="barcode-label-store" style={{ color: printColorMode === 'color' ? '#FF7A59' : '#000000', margin: 0 }}>
                                                    {storeName}
                                                </div>
                                            )}
                                            {showProductName && (
                                                <div className="barcode-label-name" title={item.product_name} style={{ margin: 0, lineHeight: 1.15 }}>
                                                    {item.product_name}
                                                </div>
                                            )}
                                            <Code128Barcode
                                                value={item.product_code}
                                                {...getBarcodeProps()}
                                            />
                                            {showPrice && (
                                                <div className="barcode-label-price" style={{ color: printColorMode === 'color' ? '#FF7A59' : '#000000', margin: 0, lineHeight: 1 }}>
                                                    ฿{Number(item.price_retail).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BarcodePrint;
